import { TableState } from "@/components/data-display/table-state";
import { StatCard } from "@/components/data-display/stat-card";
import { NotAvailableState } from "@/components/ui/system-state";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Table, TableWrapper } from "@/components/ui/table";
import { WipHint } from "@/components/ui/wip-hint";
import { SectionTitle } from "@/features/oversight/oversight-ui";

/**
 * Broadcast Monitor (MON-03, `/monitoring/broadcast`).
 *
 * Work-in-progress layout port (design-ref 2026-10-03, task
 * r2-monitoring-broadcast). The uat design is
 * `.design-ref/prototype/features/oversight/monitoring.tsx` →
 * `BroadcastMonitor`: a breadcrumb, a five-tile summary row, a pipeline
 * segmented bar, and a "Broadcast Queue" table. The structure and the tile
 * labels are carried over; the numbers and rows are not.
 *
 * The prototype's tiles are literal Figma values ("1 Queued", "8 Confirmed") and
 * its queue rows are invented ledgers, policies and approvers, none backed by a
 * read model. This route previously rendered those fixture records (ALL-5709)
 * and there is still no broadcast-monitoring route in this environment
 * (`context/page_pipeline/BACKEND_GAPS.md`), so every tile renders the shared
 * stat placeholder, and the queue renders its designed empty state.
 *
 * The pipeline segmented bar is deliberately **not** rendered: it is a chart
 * whose segments are counts, and a bar drawn from no counts would be a picture
 * of data that does not exist. Its legend labels ("Confirmed", "In-Flight",
 * "Queued") are repeated as the tile labels above it, so no information is
 * lost by omitting the bar.
 */

/** Summary tiles, in the designer's order. Values are not available. */
const BROADCAST_TILES = ["Queued", "In-Flight", "Stuck", "Failed", "Confirmed"] as const;

/** Broadcast-queue columns, in the designer's order. */
const QUEUE_COLUMNS = ["TX ID", "Chain", "Status", "Submitted", "Gas (Gwei)", "Confirmations"] as const;

export default function MonitoringBroadcastPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Broadcast Monitor" }]} />

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Broadcast Monitor</h1>
        <WipHint />
      </div>
      <p className="mb-5 max-w-[720px] text-sm leading-relaxed text-muted">
        This area has no backend in this environment yet, so no broadcast activity is shown or estimated.
      </p>

      <NotAvailableState
        title="Broadcast monitoring is not available in this environment yet"
        description="The broadcast pipeline has no monitoring route in this environment yet. Nothing on this page reflects real broadcasts."
      />

      <div className="mb-5 mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {BROADCAST_TILES.map((label) => (
          <StatCard key={label} value="" label={label} hint="Not available yet" />
        ))}
      </div>

      <SectionTitle>Broadcast Queue</SectionTitle>
      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              {QUEUE_COLUMNS.map((column) => (
                <th key={column} className="px-4 py-2.5">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={QUEUE_COLUMNS.length}
              title="No broadcasts have been recorded"
              description="The broadcast queue is read from the broadcast gateway's runtime, which this app has no monitoring route for, so there is nothing to list."
            />
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}
